import { useState, useEffect } from 'react';
import {
  Calendar, Filter, CheckCircle, XCircle, X, Download, Package,
  Plane, TrendingUp, PieChart, RefreshCw
} from 'lucide-react';
import { useTranslation } from '../../i18n/i18n';
import ReusableTable from '../../components/Tableau';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

const BookingsPage = ({ lang }) => {
  const { t } = useTranslation(lang);
  const navigate = useNavigate();
  const booking_url = process.env.REACT_APP_API_BOOKING_URL;
  const [filters, setFilters] = useState({
    product_id: '',
    provider_id: '',
    service: '',
    status: '',
    payment_status: '',
    country_code: '',
    dateFrom: '',
    dateTo: '',
    dateType: 'created_at'
  });

  const [bookings, setBookings] = useState([]);
  const [products, setProducts] = useState([]);
  const [providers, setProviders] = useState([]);
  const [countries, setCountries] = useState([]);

    const [countriesLoading, setCountriesLoading] = useState(false);
  const [providersLoading, setProvidersLoading] = useState(false);

  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);


  const fetchAllCountries = async () => {
    setCountriesLoading(true);
    try {
      const firstResponse = await api.get(process.env.REACT_APP_API_COUNTRY_URL);
      const firstData = firstResponse.data;
      const lastPage = firstData.last_page;

      let allCountries = [...firstData.data];

      if (lastPage > 1) {
        const promises = [];

        for (let page = 2; page <= lastPage; page++) {
          promises.push(
            api.get(process.env.REACT_APP_API_COUNTRY_URL, {
              params: { page }
            }).then(res => res.data.data)
          );
        }

        const pagesResults = await Promise.all(promises);
        pagesResults.forEach(pageData => {
          allCountries.push(...pageData);
        });
      }

      const formattedCountries = allCountries.map(country => ({
        id: country.id,
        name: country[lang] || country.en || country.fr || country.es || country.id,
        code: country.id
      })).sort((a, b) => a.name.localeCompare(b.name));

      setCountries(formattedCountries);

    } catch (error) {
      console.error('Erreur lors du chargement des pays:', error);
      toast.error(t('errorLoadingCountries'));
    } finally {
      setCountriesLoading(false);
    }
  };

  // charger tous les providers avec pagination
  const fetchAllProviders = async () => {
    setProvidersLoading(true);
    try {
      const urlBase = process.env.REACT_APP_API_PROVIDER_URL;
      const baseRequestBody = {
        search: "",
        status: ""
      };

      const firstResponse = await api.post(urlBase, baseRequestBody, {
        params: { page: 1 }
      });

      const firstData = firstResponse.data;
      const lastPage = firstData.last_page;

      let allProviders = [...firstData.result.data];

      if (lastPage > 1) {
        const promises = [];

        for (let page = 2; page <= lastPage; page++) {
          promises.push(
            api.get(urlBase, baseRequestBody, {
              params: { page: page }
            }).then(res => res.data.result.data)
          );
        }

        const pagesResults = await Promise.all(promises);
        pagesResults.forEach(pageData => {
          allProviders.push(...pageData);
        });
      }

      const formattedProviders = allProviders.map(provider => ({
        id: provider.id,
        name: provider.name,
        email: provider.email,
        phone: provider.phone,
        country_id: provider.country_id,
        country_name: provider.country?.[lang] || provider.country?.fr || provider.country?.id
      })).sort((a, b) => a.name.localeCompare(b.name));

      setProviders(formattedProviders);
      

    } catch (error) {
      console.error('Erreur lors du chargement des providers:', error);
      toast.error(t('errorLoadingProviders'));
    } finally {
      setProvidersLoading(false);
    }
  };
  const fetchLists = async () => {
    try {
      // Charger les produits
      // const productsResponse = await api.get('/products'); // À adapter selon votre API
      // setProducts(productsResponse.data || []);

      fetchAllCountries();

      fetchAllProviders();
    } catch (err) {
      console.error("Erreur lors du chargement des listes:", err);
    }
  };

  const checkIfFilterApplied = () => {
    return filters.service !== '' || filters.status !== '' || filters.provider_id !== '' || filters.product_id !== '' || filters.payment_status !== '' || filters.country_code !== '' || filters.dateFrom !== '' || filters.dateTo !== '';
  };


  const fetchAllBookings = async () => {
    try {
      setFilterLoading(true);

      const urlBase = booking_url;
      const params = {};

      // Ajouter tous les filtres aux params
      Object.keys(filters).forEach(key => {
        if (filters[key]) params[key] = filters[key];
      });
      params.page = 1;

      const body = {
        product_id: filters.product_id,
        provider_id: filters.provider_id,
        service: filters.service,
        status: filters.status,
        payment_status: filters.payment_status,
        country_id: filters.country_code,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        dateType: filters.dateType
      };      

      // Le reste de la fonction reste identique...
      const firstResponse = await api.post(urlBase, body, {
        params: { page: 1 }
      });
      const firstData = firstResponse.data;
      const lastPage = firstData.result.last_page;

      let allBookings = firstData.result.data.map(formatBooking);

      const promises = [];
      for (let page = 2; page <= lastPage; page++) {
        promises.push(
          api.post(urlBase, body, {
            params: { page: page }
          })
            .then(res => res.data.result.data.map(formatBooking))
        );
      }

      const pagesResults = await Promise.all(promises);
      pagesResults.forEach(page => {
        allBookings.push(...page);
      });

      setBookings(allBookings);
      setIsFilterApplied(checkIfFilterApplied());

    } catch (err) {
      console.error("Erreur API :", err);
    } finally {
      setFilterLoading(false);
    }
  };

  // Fonction utilitaire
  const formatBooking = booking => ({
    ...booking,
    provider_name: booking.provider?.name || "N/A",
    amountTotal: `${booking.amountTotal} ${booking.currency || "EUR"}`
  });

  // Fonction pour exporter en Excel
  const exportToExcel = () => {
    // Préparer les données pour l'export
    const exportData = bookings.map(booking => ({
      [t('bookingNumber')]: booking.bookingNumber,
      [t('service')]: booking.service,
      [t('checkin')]: booking.checkin,
      [t('checkout')]: booking.checkout,
      [t('provider')]: booking.provider_name,
      [t('status')]: booking.status,
      [t('totalAmount')]: booking.amountTotal,
      [t('paymentStatus')]: booking.paymentStatus
    }));

    // Créer un nouveau classeur
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Ajuster la largeur des colonnes
    const maxWidths = {};
    exportData.forEach(row => {
      Object.keys(row).forEach((key, index) => {
        const cellValue = String(row[key] || '');
        maxWidths[index] = Math.max(maxWidths[index] || 0, cellValue.length);
      });
    });

    ws['!cols'] = Object.keys(maxWidths).map(idx => ({
      wch: Math.min(Math.max(maxWidths[idx] || 10, 15), 50)
    }));

    // Ajouter la feuille au classeur
    XLSX.utils.book_append_sheet(wb, ws, t('bookings'));

    // Générer le fichier Excel
    const fileName = `${t('booking')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Calculer les statistiques SIMPLIFIÉ comme dans vos autres pages
  const confirmedBookings = bookings.filter(
    booking => booking.status === 'confirmed'
  ).length;

  const cancelledBookings = bookings.filter(
    booking => booking.status === 'cancelled'
  ).length;

  // const pendingBookings = bookings.filter(
  //   booking => booking.status === 'inprocess'
  // ).length;

  // Cartes de statistiques - SIMPLIFIÉ
  const bookingStats = [
    {
      title: t('totalBookings'),
      value: bookings.length.toString(),
      description: t('totalBookingsDesc'),
      icon: Calendar,
      color: '#7c3aed',
    },
    {
      title: t('confirmedBookings'),
      value: confirmedBookings.toString(),
      description: t('confirmedBookingsDesc'),
      icon: CheckCircle,
      color: '#10b981',
    },
    {
      title: t('cancelledBookings'),
      value: cancelledBookings.toString(),
      description: t('cancelledBookingsDesc'),
      icon: XCircle,
      color: '#ef4444',
    },
    // {
    //   title: t('pendingBookings') || 'En attente',
    //   value: pendingBookings.toString(),
    //   description: t('pendingBookingsDesc') || 'Réservations en attente',
    //   icon: Clock,
    //   color: '#f59e0b',
    // }
  ];

  const getServiceIcon = (serviceType) => {
    switch (serviceType) {
      case 'hotel': return <Package size={16} />;
      case 'flight': return <Plane size={16} />;
      case 'transfer': return <TrendingUp size={16} />;
      case 'activity': return <PieChart size={16} />;
      default: return <Package size={16} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#10b981';
      case 'cancelled': return '#ef4444';
      case 'pending': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  // Obtenir la couleur du statut de paiement
  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return '#10b981';
      case 'unpaid': return '#ef4444';
      case 'partial': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  // Colonnes du tableau
  const bookingColumns = [
    { key: 'bookingNumber', label: t('bookingNumber'), width: '140px' },
    {
      key: 'service',
      label: t('service'),
      render: (value) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {getServiceIcon(value)}
          <span>{t(value)}</span>
        </div>
      )
    },
    { key: 'checkin', label: t('checkin'), width: '120px' },
    { key: 'checkout', label: t('checkout'), width: '120px' },
    { key: 'provider_name', label: t('provider'), width: '150px' },
    {
      key: 'status',
      label: t('status'),
      render: (value) => (
        <span style={{
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '500',
          color: 'white',
          backgroundColor: getStatusColor(value)
        }}>
          {t(value)}
        </span>
      )
    },
    { key: 'amountTotal', label: t('totalAmount'), width: '140px' },
    {
      key: 'paymentStatus',
      label: t('paymentStatus'),
      render: (value) => (
        <span style={{
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '500',
          color: 'white',
          backgroundColor: getPaymentStatusColor(value)
        }}>
          {t(value)}
        </span>
      )
    }
  ];

  useEffect(() => {
    fetchAllBookings();
        fetchLists();
  }, [filters]);

  const handleDetails = (booking) => {
    navigate(`/bookings/${booking.bookingNumber}`);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const applyFilters = () => {
    fetchAllBookings();
  };

  const clearFilters = () => {
    setFilters({
      product_id: '',
      provider_id: '',
      service: '',
      status: '',
      payment_status: '',
      country_code: '',
      dateFrom: '',
      dateTo: '',
      dateType: 'created_at'
    });
    fetchAllBookings();
  };



  const filterIndicatorStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '600',
    marginTop: '12px',
    transition: 'all 0.3s ease',
    width: 'fit-content'
  };

  const styles = {
    container: {
      padding: '24px',
      background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
      minHeight: '100vh'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '32px',
      flexWrap: 'wrap',
      gap: '16px'
    },
    titleContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    },
    title: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: 0
    },
    exportButton: {
      padding: '12px 24px',
      background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontWeight: '600',
      fontSize: '14px',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 6px rgba(16, 185, 129, 0.2)',
      ':hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 12px rgba(16, 185, 129, 0.3)'
      }
    },
    filtersContainer: {
      background: 'white',
      padding: '24px',
      borderRadius: '16px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
      marginBottom: '28px',
      border: '1px solid #e5e7eb'
    },
    filtersGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: '20px',
      marginBottom: '20px'
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    filterLabel: {
      fontSize: '14px',
      fontWeight: '700',
      color: '#374151',
      marginBottom: '6px',
      letterSpacing: '0.5px'
    },
    select: {
      padding: '12px 14px',
      border: '2px solid #e5e7eb',
      borderRadius: '10px',
      fontSize: '14px',
      background: 'white',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      ':focus': {
        outline: 'none',
        borderColor: '#7c3aed',
        boxShadow: '0 0 0 3px rgba(124, 58, 237, 0.1)'
      },
      ':hover': {
        borderColor: '#d1d5db'
      }
    },
    filterButtons: {
      display: 'flex',
      gap: '14px',
      justifyContent: 'flex-start',
      marginTop: '8px'
    },
    primaryButton: {
      padding: '12px 24px',
      background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontWeight: '600',
      fontSize: '14px',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 6px rgba(124, 58, 237, 0.2)',
      ':hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 12px rgba(124, 58, 237, 0.3)'
      }
    },
    secondaryButton: {
      padding: '12px 24px',
      background: 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontWeight: '600',
      fontSize: '14px',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 6px rgba(107, 114, 128, 0.2)',
      ':hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 12px rgba(107, 114, 128, 0.3)'
      }
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
      gap: '24px',
      marginBottom: '32px'
    },
    statCard: {
      background: 'white',
      padding: '20px',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease'
    },
    statIcon: {
      width: '56px',
      height: '56px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    },
    statContent: {
      flex: 1
    },
    statValue: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#1f2937',
      marginBottom: '4px'
    },
    statTitle: {
      fontSize: '14px',
      color: '#6b7280',
      marginBottom: '4px',
      fontWeight: '600'
    },
    statDescription: {
      fontSize: '12px',
      color: '#9ca3af'
    }
  };

  return (
    <div style={styles.container}>
      {/* Header avec bouton d'export */}
      <div style={styles.header}>
        <div style={styles.titleContainer}>
          <Calendar size={36} color="#7c3aed" />
          <h1 style={styles.title}>{t('bookingsManagement')}</h1>
        </div>
        {bookings.length > 0 && (
          <button
            onClick={exportToExcel}
            style={styles.exportButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 12px rgba(16, 185, 129, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(16, 185, 129, 0.2)';
            }}
          >
            <Download size={18} />
            {t('exportToExcel') || 'Export Excel'}
          </button>
        )}
      </div>

      {/* Cartes de statistiques */}
      <div style={styles.statsGrid}>
        {bookingStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              style={styles.statCard}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-6px)';
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.05)';
              }}
            >
              <div style={{
                ...styles.statIcon,
                background: `linear-gradient(135deg, ${stat.color} 0%, ${stat.color}dd 100%)`
              }}>
                <Icon size={28} color="white" />
              </div>
              <div style={styles.statContent}>
                <div style={styles.statValue}>{stat.value}</div>
                <div style={styles.statTitle}>{stat.title}</div>
                <div style={styles.statDescription}>{stat.description}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filtres */}
      <div style={styles.filtersContainer}>
        <div style={styles.filtersGrid}>
          {/* Filtre Produit */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>{t('product') || 'Produit'}</label>
            <select
              value={filters.product_id}
              onChange={(e) => handleFilterChange('product_id', e.target.value)}
              style={styles.select}
            >
              <option value="">{t('allProducts') || 'Tous les produits'}</option>
              {products.map(product => (
                <option key={product.id} value={product.id}>
                  {product.name || product.title}
                </option>
              ))}
            </select>
          </div>

          {/* Filtre Fournisseur */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>{t('provider') || 'Fournisseur'}</label>
                        {providersLoading ? (
              <div style={styles.loadingSelect}>
                <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} />
                {t('loading')}...
              </div>
            ) : (
            <select
              value={filters.provider_id}
              onChange={(e) => handleFilterChange('provider_id', e.target.value)}
              style={styles.select}
            >
              <option value="">{t('allProviders') || 'Tous les fournisseurs'}</option>
              {providers.map(provider => (
                <option key={provider.id} value={provider.id}>
                  {provider.name}
                </option>
              ))}
            </select>
            )}
          </div>

          {/* Filtre Type de Service (existant) */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>{t('serviceType')}</label>
            <select
              value={filters.service}
              onChange={(e) => handleFilterChange('service', e.target.value)}
              style={styles.select}
            >
              <option value="">{t('allServices')}</option>
              <option value="hotel">{t('hotel')}</option>
              <option value="activity">{t('activity')}</option>
              <option value="flight">{t('flight')}</option>
              <option value="transfer">{t('transfer')}</option>
            </select>
          </div>

          {/* Filtre Statut (existant) */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>{t('status')}</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              style={styles.select}
            >
              <option value="">{t('allStatus')}</option>
              <option value="confirmed">{t('confirmed')}</option>
              <option value="inprocess">{t('pending')}</option>
              <option value="cancelled">{t('cancelled')}</option>
            </select>
          </div>

          {/* Nouveau: Filtre Statut de Paiement */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>{t('paymentStatus')}</label>
            <select
              value={filters.payment_status}
              onChange={(e) => handleFilterChange('payment_status', e.target.value)}
              style={styles.select}
            >
              <option value="">{t('allPaymentStatus') || 'Tous les statuts'}</option>
              <option value="paid">{t('paid')}</option>
              <option value="unpaid">{t('unpaid')}</option>
              <option value="partial">{t('partial') || 'Partiel'}</option>
            </select>
          </div>

          {/* Nouveau: Filtre Pays */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>{t('country') || 'Pays'}</label>
                       {countriesLoading ? (
              <div style={styles.loadingSelect}>
                <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} />
                {t('loading')}...
              </div>
            ) : (
            <select
              value={filters.country_code}
              onChange={(e) => handleFilterChange('country_code', e.target.value)}
              style={styles.select}
            >
              <option value="">{t('allCountries') || 'Tous les pays'}</option>
              {countries.map(country => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
            </select>
            )}
          </div>

          {/* Nouveau: Filtre Date From */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>{t('dateFrom') || 'Date début'}</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              style={styles.select}
            />
          </div>

          {/* Nouveau: Filtre Date To */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>{t('dateTo') || 'Date fin'}</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              style={styles.select}
            />
          </div>

          {/* Nouveau: Type de Date */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>{t('dateType') || 'Type de date'}</label>
            <select
              value={filters.dateType}
              onChange={(e) => handleFilterChange('dateType', e.target.value)}
              style={styles.select}
            >
              <option value="created_at">{t('createdAt') || 'Date de création'}</option>
              <option value="updated_at">{t('updatedAt') || 'Date de modification'}</option>
              <option value="checkin">{t('checkin') || 'Date d\'arrivée'}</option>
              <option value="checkout">{t('checkout') || 'Date de départ'}</option>
            </select>
          </div>
        </div>


{isFilterApplied && (
  <div style={{ ...filterIndicatorStyle, background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', border: '1px solid #fbbf24', color: '#92400e' }}>
    <Filter size={16} />
    {t('filterActive') || 'Filtre actif'}

    {/* Version ultra-compacte */}
    {[
      [t('product'), products.find(p => p.id === filters.product_id)?.name],
      [t('provider'), providers.find(p => p.id === parseInt(filters.provider_id))?.name],
      [t('serviceType'), filters.service && t(filters.service)],
      [t('status'), filters.status && t(filters.status)],
      [t('paymentStatus'), filters.payment_status && t(filters.payment_status)],
      [t('country'), countries.find(c => c.code === filters.country_code)?.name],
      [t('from'), filters.dateFrom],
      [t('to'), filters.dateTo],
      [t('dateType'), filters.dateType !== 'created_at' && t(filters.dateType)],
    ].map(([label, val]) => val && ` - ${label}: ${val}`)}

    {filterLoading && <span style={{ marginLeft: '8px' }}>{t('loading')}</span>}
  </div>
)}


        {filterLoading && !isFilterApplied && (
          <div style={{
            ...filterIndicatorStyle,
            background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
            border: '1px solid #818cf8',
            color: '#3730a3'
          }}>
            {t('loading') || 'Chargement...'}
          </div>
        )}

        <div style={styles.filterButtons}>
          <button
            style={styles.secondaryButton}
            onClick={() => {
              clearFilters();
              setIsFilterApplied(false);
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 12px rgba(107, 114, 128, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(107, 114, 128, 0.2)';
            }}
            disabled={filterLoading}
          >
            <X size={18} />
            {t('clearFilters')}
          </button>
          <button
            style={{
              ...styles.primaryButton,
              opacity: filterLoading ? 0.7 : 1,
              cursor: filterLoading ? 'not-allowed' : 'pointer'
            }}
            onClick={applyFilters}
            onMouseEnter={(e) => {
              if (!filterLoading) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 12px rgba(124, 58, 237, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (!filterLoading) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(124, 58, 237, 0.2)';
              }
            }}
            disabled={filterLoading}
          >
            <Filter size={18} />
            {filterLoading ? (t('loading') || 'Chargement...') : t('applyFilters')}
          </button>
        </div>
      </div>

      {/* Tableau des réservations */}

      <ReusableTable
        data={bookings}
        columns={bookingColumns}
        onDetails={handleDetails}
        onRowClick={handleDetails}
        lang={lang}
        colors={{
          headerBg: '#7c3aed',
          buttonDetails: '#10b981',
          rowHover: '#f0f3ff',
          border: '#e5e7eb'
        }}
      />
    </div>
  );
};

export default BookingsPage;
